import _ from "lodash";
import { DateTime } from "luxon";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import { Placeholder } from "rsuite";
import {
  humanizeDuration,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import { Price } from "../../common/Price";
import PropTypes from "prop-types";
import RatingPanel from "../../common/RatingPanel";
import jssVariables from "../../../jss_setup";
import { InlineIcon } from "@iconify/react";
import { CustomCheckbox } from "../../forms/inputGroup/helpers";

const cancelationPolicyStyles = createUseStyles({
  cxlsContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    border: `1px solid ${jssVariables.colors.base_color}`,
    borderRadius: jssVariables.normal_gap,
    ..._.get(props, "customStyles.cxlsContainer", {}),
  }),
  cxls: (props) => ({
    display: "grid",
    fontSize: "medium",
    paddingTop: `calc(${jssVariables.normal_gap} * 1.5)`,
    paddingBottom: jssVariables.normal_gap,
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.cxls", {}),
  }),
  ul: (props) => ({
    paddingLeft: `calc(${jssVariables.normal_gap} * 2.5)`,
    marginBottom: jssVariables.half_gap,
    ..._.get(props, "customStyles.ul", {}),
  }),
  terms: (props) => ({
    fontSize: "smaller",
    ..._.get(props, "customStyles.terms", {}),
  }),
  free: (props) => ({
    color: "green",
    ..._.get(props, "customStyles.free", {}),
  }),
  chargeable: (props) => ({
    color: "red",
    ..._.get(props, "customStyles.chargeable", {}),
  }),
  cxlDisclaimer: (props) => ({
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.cxlDisclaimer", {}),
  }),
  infoIcon: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "xx-large",
    margin: jssVariables.normal_gap,
    ..._.get(props, "customStyles.infoIcon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    cxls: (props) => ({
      paddingTop: jssVariables.normal_gap,
      paddingBottom: jssVariables.half_gap,
      ..._.get(props, "customStyles.cxls.smallscreen", {}),
    }),
  },
});
export const CancelationPolicy = ({ cxls }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const classes = cancelationPolicyStyles({
    customStyles: cStyler({
      type,
      brand,
      objName: "TrfCxls",
    }),
  });
  const { t } = useContext(TranslationContext);

  return (
    <div className={classes.cxlsContainer}>
      <div className={classes.cxls}>
        {cxls.map((cxl, idx) => {
          if (cxl.type === "non-refundable") {
            return (
              <span className={classes.chargeable} key={idx}>
                {t("thisBookingIsNonNefundable")}.
              </span>
            );
          }

          return (
            <span key={idx}>
              <ul className={classes.ul}>
                <li>
                  {" "}
                  {cxl.type === "refundable"
                    ? `Up ${t("to")} `
                    : `${t("from")}
                  ${DateTime.fromISO(cxl.from).toLocaleString(
                    DateTime.DATETIME_SHORT
                  )} ${t("to")} `}
                  {DateTime.fromISO(cxl.to).toLocaleString(
                    DateTime.DATETIME_SHORT
                  )}{" "}
                  <span
                    className={
                      cxl.type === "refundable"
                        ? classes.free
                        : classes.chargeable
                    }
                    key={idx}>
                    {t("cancelationIs")}{" "}
                    {cxl.type === "refundable" ? (
                      t("free")
                    ) : (
                      <span>
                        {`${t("chargeableWithFeeOf")} `}
                        <Price
                          styleless={true}
                          price={cxl.fee}
                          currency={cxl.currency}
                        />
                      </span>
                    )}{" "}
                  </span>
                </li>
              </ul>
            </span>
          );
        })}
      </div>
      <InlineIcon icon="ic:baseline-info" className={classes.infoIcon} />
    </div>
  );
};
CancelationPolicy.defaultProps = {
  cxls: [],
};
CancelationPolicy.propTypes = {
  cxls: PropTypes.array.isRequired,
};

const iconStyles = {
  color: jssVariables.colors.base_color,
  fontSize: "large",
  justifySelf: "center",
};

const styles = createUseStyles({
  prebook: (props) => ({
    display: "grid",
    justifyItems: "center",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.prebook", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    fontSize: "medium",
    paddingBottom: jssVariables.normal_gap,
    ..._.get(props, "customStyles.header", {}),
  }),
  headerItem: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    alignItems: "center",
    gridGap: jssVariables.normal_gap,
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    border: `1px solid ${jssVariables.colors.border}`,
    borderRadius: jssVariables.half_gap,
    position: "relative",
    width: "95%",
    marginLeft: "5%",
    "&:before": {
      content: `''`,
      position: "absolute",
      left: "-1.3rem",
      top: "50%",
      transform: "translateY(-50%)",
      height: ".6rem",
      width: ".6rem",
      borderRadius: "50%",
      background: "lightgray",
    },
    "&:not(:last-child):after": {
      content: `''`,
      position: "absolute",
      left: "-1.07rem",
      top: "60%",
      height: "100%",
      width: "0rem",
      borderLeft: ".1rem dashed lightgray",
    },
    ..._.get(props, "customStyles.headerItem", {}),
  }),
  headerItemText: (props) => ({
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    ..._.get(props, "customStyles.headerItemText", {}),
  }),
  originIcon: (props) => ({
    ...iconStyles,
    color: "lightgray",
    ..._.get(props, "customStyles.originIcon", {}),
  }),
  arrowIcon: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.arrowIcon", {}),
  }),
  destinationIcon: (props) => ({
    ...iconStyles,
    ..._.get(props, "customStyles.destinationIcon", {}),
  }),
  placeholderImg: (props) => ({
    height: "9rem!important",
    width: "80vw!important",
    ..._.get(props, "customStyles.placeholderImg", {}),
  }),
  placeholderParagraph: (props) => ({
    width: "100%",
    marginBottom: jssVariables.double_gap,
    ..._.get(props, "customStyles.placeholderParagraph", {}),
  }),
  content: (props) => ({
    display: "grid",
    alignItems: "start",
    gridGap: jssVariables.normal_gap,
    justifyItems: "center",
    fontSize: "medium",
    ..._.get(props, "customStyles.content", {}),
  }),
  imgs: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    alignItems: "end",
    justifyContent: "start",
    width: "100%",
    ..._.get(props, "customStyles.imgs", {}),
  }),
  img: (props) => ({
    height: "9rem",
    width: "60vw",
    objectFit: "contain",
    justifySelf: "center",
    ..._.get(props, "customStyles.img", {}),
  }),
  contentInfo: (props) => ({
    width: "100%",
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 3)`,
    ..._.get(props, "customStyles.contentInfo", {}),
  }),
  contentInfoHeader: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    justifyContent: "space-between",
    alignItems: "center",
    ..._.get(props, "customStyles.contentInfoHeader", {}),
  }),
  providerLogo: (props) => ({
    width: "6rem",
    maxHeight: "3rem",
    objectFit: "contain",
    borderRadius: jssVariables.half_gap,
    ..._.get(props, "customStyles.providerLogo", {}),
  }),
  item: (props) => ({
    ..._.get(props, "customStyles.item", {}),
  }),
  priceComponent: (props) => ({
    fontWeight: "bold",
    fontSize: "x-large",
    justifySelf: "end",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.priceComponent", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    header: (props) => ({
      maxWidth: "40rem",
      gridTemplateColumns: " 1fr max-content 1fr",
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    headerItem: (props) => ({
      marginLeft: 0,
      width: "100%",
      "&:before": {
        display: "none",
      },
      "&:not(:last-child):after": {
        display: "none",
      },
      ..._.get(props, "customStyles.headerItem.smallscreen", {}),
    }),
    arrowIcon: (props) => ({
      display: "block",
      color: jssVariables.colors.base_color,
      fontSize: "xx-large",
      alignSelf: "center",
      ..._.get(props, "customStyles.arrowIcon.smallscreen", {}),
    }),
    content: (props) => ({
      gridGap: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    imgs: (props) => ({
      display: "grid",
      height: "100%",
      justifyContent: "space-between",
      alignItems: "baseline",
      gridAutoFlow: "dense",
      ..._.get(props, "customStyles.imgs.smallscreen", {}),
    }),
    img: (props) => ({
      height: "7rem",
      width: "max-content",
      objectFit: "contain",
      marginBottom: 0,
      justifySelf: "start",
      ..._.get(props, "customStyles.img.smallscreen", {}),
    }),
    providerLogo: (props) => ({
      gridColumn: 2,
      minHeight: "7rem",
      width: "8rem",
      ..._.get(props, "customStyles.providerLogo.smallscreen", {}),
    }),
    placeholderImg: (props) => ({
      height: "10rem!important",
      width: "15rem!important",
      ..._.get(props, "customStyles.placeholderImg.smallscreen", {}),
    }),
    bottomContainer: (props) => ({
      display: "grid",
      paddingTop: jssVariables.double_gap,
      ..._.get(props, "customStyles.bottomContainer.smallscreen", {}),
    }),
    priceComponent: (props) => ({
      fontSize: "xx-large",
      ..._.get(props, "customStyles.priceComponent.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
const Prebook = ({
  loading,
  preBookResult: transfer,
  cxls,
  agreed,
  setAgreed,
}) => {
  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "TrfPreBook",
  });
  const classes = styles({ customStyles });

  return (
    <div className={classes.prebook}>
      {loading ? (
        <Placeholder.Paragraph
          rows={2}
          active
          className={classes.placeholderParagraph}
        />
      ) : (
        <div className={classes.header}>
          <div className={classes.headerItem}>
            <InlineIcon
              icon="fa-solid:map-marker-alt"
              className={classes.originIcon}
            />
            <span className={classes.headerItemText}>
              {transfer.origin.name}
            </span>
          </div>
          <InlineIcon icon="fa:angle-right" className={classes.arrowIcon} />
          <div className={classes.headerItem}>
            <InlineIcon
              icon="fa-solid:map-marker-alt"
              className={classes.destinationIcon}
            />
            <span className={classes.headerItemText}>
              {transfer.destination.name}
            </span>
          </div>
        </div>
      )}
      <div className={classes.content}>
        <div className={classes.imgs}>
          {loading ? (
            <Placeholder.Graph active className={classes.placeholderImg} />
          ) : (
            <React.Fragment>
              <img
                src={_.get(transfer, "inbound.provider.logo.url", "")}
                alt=""
                className={classes.providerLogo}
              />
              <img
                src={_.get(transfer, "inbound.vehicle.images.0.url", "")}
                alt=""
                className={classes.img}
              />
            </React.Fragment>
          )}
        </div>
        <div className={classes.contentInfo}>
          {loading ? (
            <Placeholder.Paragraph rows={6} active />
          ) : (
            <React.Fragment>
              <div className={classes.contentInfoHeader}>
                {Number(_.get(transfer, "inbound.provider.rating", 0)) ? (
                  <RatingPanel
                    rating={_.get(transfer, "inbound.provider.rating", 0)}
                    fontSize="x-large"
                  />
                ) : (
                  <span></span>
                )}
              </div>
              <span>
                <strong>{`${t("inboundPickupDate")}: `}</strong>
                {DateTime.fromISO(transfer.inbound.start).toLocaleString(
                  DateTime.DATETIME_MED_WITH_WEEKDAY
                )}
              </span>
              {_.get(transfer, "outbound.start", "") && (
                <span>
                  <strong>{`${t("outboundPickupDate")}: `}</strong>
                  {DateTime.fromISO(transfer.outbound.start).toLocaleString(
                    DateTime.DATETIME_MED_WITH_WEEKDAY
                  )}
                </span>
              )}
              <div className={classes.item}>
                <strong className={classes.label}>
                  {t("vehicle")}
                  {": "}
                </strong>
                {_.startCase(
                  _.toLower(_.get(transfer, "inbound.vehicle.brand", ""))
                )}
              </div>
              <div className={classes.item}>
                <strong className={classes.label}>
                  {t("distance")}
                  {": "}
                </strong>
                {_.get(transfer, "inbound.distance", 0)} {t("km")}
              </div>
              <div className={classes.item}>
                <strong className={classes.label}>
                  {t("duration")}
                  {": "}
                </strong>
                {humanizeDuration({
                  duration: _.get(transfer, "inbound.duration", 0),
                })}
              </div>
              <div className={classes.priceComponent}>
                <Price
                  price={transfer.price.value}
                  currency={transfer.price.currency}
                  styleless
                />
              </div>
              <CancelationPolicy cxls={cxls} />
              <CustomCheckbox
                label={t("iHaveReadAgreeWithAbove")}
                checked={agreed}
                setChecked={setAgreed}
              />
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};
Prebook.defaultProps = {
  optsForDisplay: {},
  time: "",
  cxls: [],
  loading: false,
  preBookResult: {},
};
Prebook.propTypes = {
  optsForDisplay: PropTypes.object.isRequired,
  time: PropTypes.string,
  cxls: PropTypes.array,
  loading: PropTypes.bool,
  preBookResult: PropTypes.object.isRequired,
  agreed: PropTypes.bool.isRequired,
  setAgreed: PropTypes.func.isRequired,
};

export default Prebook;
